import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { Smartphone, ShieldCheck, Clock, Loader2, CreditCard, Check, X, AlertTriangle } from 'lucide-react';
import { authAPI, api as mainApi } from '../api/apiService';

// We need the api instance to make the call
import api from '../api/axios';
import Lottie from 'lottie-react';
import verificationAnimation from '../assets/verification.json';

const loadRazorpayScript = () => {
    return new Promise((resolve) => {
        if (window.Razorpay) {
            resolve(true);
            return;
        }
        const script = document.createElement("script");
        script.src = "https://checkout.razorpay.com/v1/checkout.js";
        script.onload = () => resolve(true);
        script.onerror = () => resolve(false);
        document.body.appendChild(script);
    });
};

export default function PaymentProcessingPage() {
    const navigate = useNavigate();
    const location = useLocation();
    const { plan, method, paymentDetails } = location.state || {};

    const [status, setStatus] = useState('processing'); // processing, finalizing
    const [showMockModal, setShowMockModal] = useState(false);
    const [mockData, setMockData] = useState(null);

    useEffect(() => {
        if (!plan || !method) {
            navigate('/owner/subscription-management');
            return;
        }

        const processPayment = async () => {
            try {
                // Step 1: Create a payment order via backend endpoint first
                const initRes = await api.post("/api/subscriptions/subscriptions/initiate/", {
                    plan_id: plan.id
                });

                const initData = initRes.data;
                const razorpayOrderId = initData.razorpay_order_id;
                const paymentRecordId = initData.payment_id;

                // Step 2: Check if sandbox mock mode is enabled
                if (razorpayOrderId && razorpayOrderId.startsWith('order_mock_')) {
                    setMockData({
                        orderId: razorpayOrderId,
                        paymentRecordId: paymentRecordId,
                        initData
                    });
                    setShowMockModal(true);
                    return;
                }

                // Step 3: Load real Razorpay script & launch
                const isLoaded = await loadRazorpayScript();
                if (!isLoaded) {
                    throw new Error("Razorpay SDK failed to load. Please check your internet connection.");
                }

                const options = {
                    key: initData.key_id,
                    amount: initData.amount,
                    currency: initData.currency,
                    name: "gio billing POS System",
                    description: `${plan.name} Subscription Renewal`,
                    order_id: razorpayOrderId,
                    handler: async function (response) {
                        setStatus('finalizing');
                        try {
                            const verifyRes = await api.post("/api/subscriptions/subscriptions/verify_payment/", {
                                razorpay_payment_id: response.razorpay_payment_id,
                                razorpay_order_id: response.razorpay_order_id,
                                razorpay_signature: response.razorpay_signature,
                                payment_id: paymentRecordId,
                                payment_method: method.toUpperCase()
                            });

                            if (verifyRes.data && verifyRes.data.status === 'success') {
                                navigate('/owner/payment-success', { state: { planName: plan.name, plan } });
                            } else {
                                navigate('/owner/payment-failure', { 
                                    state: { 
                                        error: verifyRes.data?.error || "Signature verification failed on backend.", 
                                        plan 
                                    } 
                                });
                            }
                        } catch (err) {
                            console.error("Backend verification error:", err);
                            const errMsg = err.response?.data?.error || "We couldn't verify your payment signature.";
                            navigate('/owner/payment-failure', { state: { error: errMsg, plan } });
                        }
                    },
                    prefill: {
                        name: initData.user_details?.name || "",
                        contact: initData.user_details?.contact || "",
                        email: initData.user_details?.email || ""
                    },
                    theme: {
                        color: "#2563EB"
                    },
                    modal: {
                        ondismiss: function() {
                            navigate('/owner/payment-failure', { state: { error: "Payment was cancelled or closed.", plan } });
                        }
                    }
                };

                const rzp = new window.Razorpay(options);
                rzp.open();

            } catch (error) {
                console.error("Payment processing error:", error);
                const errorMsg = error.response?.data?.error || error.response?.data?.message || error.message || "Unable to initialize checkout. Please check gateway credentials.";
                navigate('/owner/payment-failure', { state: { error: errorMsg, plan } });
            }
        };

        processPayment();
    }, [plan, method, paymentDetails, navigate]);

    // Handle Mock Payment Outcomes
    const handleMockOutcome = async (outcomeType) => {
        if (!mockData) return;
        setShowMockModal(false);
        setStatus('finalizing');

        const mockPaymentId = `pay_mock_${Math.random().toString(36).substr(2, 9)}`;
        let finalPaymentId = mockPaymentId;
        
        if (outcomeType === 'pending') {
            finalPaymentId = `${mockPaymentId}99`;
        } else if (outcomeType === 'failed') {
            finalPaymentId = `${mockPaymentId}00`;
        }

        try {
            const verifyRes = await api.post("/api/subscriptions/subscriptions/verify_payment/", {
                razorpay_payment_id: finalPaymentId,
                razorpay_order_id: mockData.orderId,
                razorpay_signature: "mock_signature_credential",
                payment_id: mockData.paymentRecordId,
                payment_method: method.toUpperCase()
            });

            if (verifyRes.data && verifyRes.data.status === 'success') {
                navigate('/owner/payment-success', { state: { planName: plan.name, plan } });
            } else if (verifyRes.data && verifyRes.data.status === 'pending') {
                // If backend returns pending status simulation
                navigate('/owner/subscription-management', { 
                    state: { 
                        notification: "Payment Verification Pending. Your subscription will activate once verified.",
                        notificationType: "warning"
                    } 
                });
            } else {
                navigate('/owner/payment-failure', { 
                    state: { 
                        error: verifyRes.data?.error || "Mock verification returned failure.", 
                        plan 
                    } 
                });
            }
        } catch (err) {
            console.error("Mock verification request error:", err);
            const errMsg = err.response?.data?.error || "Simulated payment failed on signature validation.";
            navigate('/owner/payment-failure', { state: { error: errMsg, plan } });
        }
    };

    return (
        <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center p-4">
            <div className="max-w-md w-full bg-white dark:bg-slate-800 rounded-3xl shadow-2xl p-10 text-center border border-gray-100 dark:border-slate-700">
                <div className="space-y-8">
                    {/* Dynamic Icon */}
                    <div className="w-28 h-28 mx-auto flex items-center justify-center">
                        <Lottie
                            animationData={verificationAnimation}
                            loop={true}
                            autoplay={true}
                            style={{ width: '100%', height: '100%' }}
                        />
                    </div>

                    <div className="space-y-3">
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={status}
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -10 }}
                            >
                                <p className="text-[#052e16]/60 text-sm font-medium">Selected Plan</p>
                                <p className="text-[#052e16] dark:text-white text-2xl font-bold">{(plan?.name || '').replace(/\(Monthly\)|\(Yearly\)/gi, '').trim()}</p>
                                <h1 className="text-2xl font-black text-gray-900 dark:text-white mt-4">
                                    {status === 'processing' ? 'Connecting to Payment Gateway' : 'Verifying Transaction...'}
                                </h1>
                                <p className="text-gray-500 dark:text-gray-400 text-sm mt-2 px-4">
                                    {status === 'processing'
                                        ? "Please wait while we establish a secure connection with Razorpay."
                                        : "Payment confirmed. We're now verifying signature credentials and activating your subscription."}
                                </p>
                            </motion.div>
                        </AnimatePresence>
                    </div>

                    {/* Security Badge */}
                    <div className="pt-4 flex items-center justify-center gap-4 border-t border-gray-50 dark:border-slate-700/50">
                        <div className="flex items-center gap-1.5 text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                            <ShieldCheck className="w-4 h-4" /> 256-bit Encryption
                        </div>
                        <div className="w-1.5 h-1.5 rounded-full bg-gray-200 dark:bg-slate-700" />
                        <div className="flex items-center gap-1.5 text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                            <Clock className="w-4 h-4" /> Secure Gateway
                        </div>
                    </div>
                </div>

                {/* Warning Footer */}
                <p className="mt-10 text-[11px] text-red-500/70 font-medium bg-red-50 dark:bg-red-900/10 py-3 px-4 rounded-xl border border-red-100 dark:border-red-900/20">
                    ⚠️ DO NOT REFRESH OR CLICK BACK BUTTON
                </p>
            </div>

            {/* Sandbox Checkout Simulation Dialog */}
            {showMockModal && mockData && (
                <div className="fixed inset-0 bg-slate-900/60 dark:bg-black/85 backdrop-blur-md z-55 flex items-center justify-center p-4">
                    <motion.div
                        initial={{ scale: 0.9, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-3xl max-w-md w-full shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200"
                        onClick={e => e.stopPropagation()}
                    >
                        {/* Header */}
                        <div className="p-6 bg-slate-50 dark:bg-slate-900 border-b border-slate-100 dark:border-slate-700 flex justify-between items-center">
                            <div className="flex items-center gap-2">
                                <CreditCard className="w-5 h-5 text-blue-600 animate-pulse" />
                                <span className="font-extrabold text-sm text-slate-800 dark:text-white uppercase tracking-wider">Razorpay Sandbox</span>
                            </div>
                            <button
                                onClick={() => navigate('/owner/payment-failure', { state: { error: "Checkout cancelled by developer.", plan } })}
                                className="p-1 hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-400 rounded-full transition"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        {/* Content */}
                        <div className="p-6 space-y-4">
                            <div className="p-4 bg-blue-50 dark:bg-blue-950/20 text-blue-800 dark:text-blue-300 rounded-2xl text-xs font-semibold leading-relaxed flex items-start gap-3">
                                <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
                                <div>
                                    <p className="font-bold">Developer Sandbox Mode</p>
                                    <p className="mt-1 opacity-90">Real Razorpay configurations are missing or inactive on local. You can simulate transaction responses to test your flows.</p>
                                </div>
                            </div>

                            <div className="space-y-1.5 text-xs text-slate-600 dark:text-slate-300 bg-slate-50 dark:bg-slate-900 p-4 rounded-2xl border border-slate-150 dark:border-slate-700">
                                <div className="flex justify-between">
                                    <span>Selected Plan:</span>
                                    <span className="font-extrabold text-slate-850 dark:text-white">{plan?.name}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span>Amount due:</span>
                                    <span className="font-extrabold text-slate-850 dark:text-white">₹{parseFloat(plan?.price).toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span>Selected Method:</span>
                                    <span className="font-extrabold text-slate-850 dark:text-white uppercase">{method}</span>
                                </div>
                            </div>
                        </div>

                        {/* Actions */}
                        <div className="p-6 bg-slate-50 dark:bg-slate-900 border-t border-slate-100 dark:border-slate-700 space-y-2">
                            <button
                                onClick={() => handleMockOutcome('success')}
                                className="w-full py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold transition flex items-center justify-center gap-1.5"
                            >
                                <Check className="w-4 h-4" /> Simulate Successful Payment
                            </button>
                            <div className="grid grid-cols-2 gap-2">
                                <button
                                    onClick={() => handleMockOutcome('pending')}
                                    className="py-2.5 bg-amber-500 hover:bg-amber-600 text-white rounded-xl text-xs font-bold transition"
                                >
                                    Simulate Pending
                                </button>
                                <button
                                    onClick={() => handleMockOutcome('failed')}
                                    className="py-2.5 bg-rose-600 hover:bg-rose-700 text-white rounded-xl text-xs font-bold transition"
                                >
                                    Simulate Failure
                                </button>
                            </div>
                        </div>
                    </motion.div>
                </div>
            )}
        </div>
    );
}
